import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ActivityTimeline from './ActivityTimeline';
import DocumentPricingDetails from './DocumentPricingDetails';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { formatCurrency } from '../utils/calculationUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Add axios interceptor for authentication
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const DocumentView = ({ document, onBack, onEditDocument }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [linkedDocuments, setLinkedDocuments] = useState([]);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationType, setGenerationType] = useState('');
  const [supplierInfo, setSupplierInfo] = useState({
    nom_fournisseur: '',
    telephone_fournisseur: '',
    cni_fournisseur: '',
    type_camion: '',
    projet: '',
    tracteur_1: '',
    semi_remorque_1: '',
    tracteur_2: '',
    semi_remorque_2: ''
  });
  const [documentStatus, setDocumentStatus] = useState(document.status || document.statut || 'en_cours');
  const [templateStyle, setTemplateStyle] = useState(document.template_style || 'standard');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Nouveaux états pour la gestion avancée des transporteurs et véhicules
  const [selectedTransporteurId, setSelectedTransporteurId] = useState('');
  const [transporteurs, setTransporteurs] = useState([]);
  const [vehicles, setVehicles] = useState([{ id: 1, tracteur: '', semi_remorque: '', fromFleet: false }]);
  const [saveToFleet, setSaveToFleet] = useState(false);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  
  // Nouveaux états pour la sélection des véhicules de flotte
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [selectedFleetVehicles, setSelectedFleetVehicles] = useState([]);
  const [loadingFleet, setLoadingFleet] = useState(false);
  const [allowEditFleetVehicles, setAllowEditFleetVehicles] = useState(false);

  // Function to duplicate document
  const handleDuplicateDocument = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Create a new reference number
      const newReference = `${document.type.toUpperCase()}-${Date.now()}`;
      
      // Prepare duplicate data
      const duplicatedData = {
        type: document.type,
        numero_reference: newReference,
        societe_id: document.societe_id,
        client: document.client,
        items: document.items,
        notes: document.notes || '',
        conditions: document.conditions || '',
        status: 'en_cours', // Reset status to in progress
        statut: 'en_cours',
        date_creation: new Date().toISOString(),
        template_style: document.template_style || 'standard'
      };
      
      const response = await axios.post('/api/documents', duplicatedData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200 || response.status === 201) {
        alert('Document dupliqué avec succès !');
        
        // Navigate back to list to see the new document
        setTimeout(() => {
          onBack();
        }, 500);
      }
    } catch (error) {
      console.error('Error duplicating document:', error);
      alert('Erreur lors de la duplication du document');
    }
  };

  // Load linked documents on component mount
  useEffect(() => {
    loadLinkedDocuments();
  }, [document.id]);

  // Listen for edit button click from HTML preview
  useEffect(() => {
    const handleMessage = (event) => {
      // Check if the message is from our preview
      if (event.data && event.data.type === 'EDIT_DOCUMENT') {
        // Close the preview modal
        setShowHtmlPreview(false);
        setHtmlContent('');
        
        // If we have an edit handler, call it
        if (onEditDocument) {
          onEditDocument(document);
        }
      }
    };

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [document, onEditDocument]);

  const loadLinkedDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/${document.id}/linked-documents`);
      setLinkedDocuments(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des documents liés:', error);
    }
  };

  // HTML Preview Functions
  const previewHTML = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/${document.id}/html-preview`);
      setHtmlContent(response.data);
      setShowHtmlPreview(true);
    } catch (error) {
      console.error('Erreur lors du preview HTML:', error);
      alert('Erreur lors du preview HTML');
    } finally {
      setLoading(false);
    }
  };

  // Status Management
  const updateStatus = async (newStatus) => {
    setLoading(true);
    try {
      console.log('Updating status to:', newStatus);
      // Use POST method as defined in backend API
      let response;
      try {
        response = await axios.post(`${API}/documents/${document.id}/status`, { status: newStatus });
      } catch (error) {
        console.log('Status endpoint failed, trying statut:', error);
        // If that fails, try with 'statut' field
        response = await axios.post(`${API}/documents/${document.id}/status`, { statut: newStatus });
      }
      
      setDocumentStatus(newStatus);
      alert('Statut mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setLoading(false);
    }
  };

  // 🔄 NOUVELLE FONCTION: Recalculer les totaux du document
  const recalculateDocument = async () => {
    setLoading(true);
    try {
      console.log('Recalculating document totals...');
      const response = await axios.get(`${API}/documents/${document.id}/calculate`);
      
      // Mettre à jour le document avec les nouvelles valeurs
      const updatedDocument = response.data;
      
      // Notifier la réussite avec détails
      const oldTotal = document.total_ht || 0;
      const newTotal = updatedDocument.total_ht || 0;
      const difference = newTotal - oldTotal;
      
      let message = 'Document recalculé avec succès !';
      if (Math.abs(difference) > 100) {
        message += `\nTotal HT: ${formatCurrency(oldTotal)} → ${formatCurrency(newTotal)}`;
        if (difference > 0) {
          message += `\n(+${formatCurrency(difference)})`;
        } else {
          message += `\n(${formatCurrency(difference)})`;
        }
      } else {
        message += '\nAucune modification nécessaire.';
      }
      
      alert(message);
      
      // Recharger la page pour afficher les nouvelles valeurs
      window.location.reload();
      
    } catch (error) {
      console.error('Erreur lors du recalcul:', error);
      alert('Erreur lors du recalcul du document');
    } finally {
      setLoading(false);
    }
  };

  // Document Generation Functions
  const generateDocument = async (type) => {
    console.log('Generating document:', type);
    console.log('Current document status:', documentStatus);
    console.log('Document statut field:', document.statut);
    
    // Check if document is in accepted status (try both 'acceptée' and 'accepted')
    const currentStatus = documentStatus || document.statut || document.status;
    if (currentStatus !== 'acceptée' && currentStatus !== 'accepted') {
      alert(`Le document doit être accepté avant de pouvoir générer un ${type === 'bon_commande' ? 'Bon de Commande' : 'Facture'}. Statut actuel: ${currentStatus}`);
      return;
    }
    
    setGenerationType(type);
    if (type === 'bon_commande') {
      setShowGenerationModal(true);
    } else {
      performGeneration(type);
    }
  };

  const performGeneration = async (type, additionalData = {}) => {
    setLoading(true);
    try {
      const endpoint = type === 'bon_commande' ? 'generate-bon-commande' : 'generate-facture';
      console.log('Calling endpoint:', `${API}/documents/${document.id}/${endpoint}`);
      console.log('Additional data:', additionalData);
      
      const response = await axios.post(`${API}/documents/${document.id}/${endpoint}`, additionalData);
      
      console.log('Generation response:', response.data);
      alert(`${type === 'bon_commande' ? 'Bon de Commande' : 'Facture'} généré avec succès!`);
      setShowGenerationModal(false);
      setSupplierInfo({
        nom_fournisseur: '',
        telephone_fournisseur: '',
        cni_fournisseur: '',
        type_camion: '',
        projet: '',
        tracteur_1: '',
        semi_remorque_1: '',
        tracteur_2: '',
        semi_remorque_2: ''
      });
      loadLinkedDocuments(); // Refresh linked documents
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur inconnue';
      alert(`Erreur lors de la génération du document: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerationSubmit = () => {
    if (!supplierInfo.nom_fournisseur || !supplierInfo.telephone_fournisseur) {
      alert('Veuillez remplir les champs obligatoires');
      return;
    }
    
    // Convert vehicles array to supplier info format
    const vehicleData = {};
    vehicles.forEach((vehicle, index) => {
      if (vehicle.tracteur || vehicle.semi_remorque) {
        vehicleData[`tracteur_${index + 1}`] = vehicle.tracteur || '';
        vehicleData[`semi_remorque_${index + 1}`] = vehicle.semi_remorque || '';
      }
    });
    
    const submissionData = {
      ...supplierInfo,
      ...vehicleData,
      vehicles: vehicles, // Include full vehicles array
      save_to_fleet: saveToFleet && selectedTransporteurId
    };
    
    performGeneration('bon_commande', { bon_commande_info: submissionData });
  };

  // Nouvelles fonctions pour la gestion des transporteurs et véhicules
  useEffect(() => {
    loadTransporteurs();
  }, []);

  const loadTransporteurs = async () => {
    try {
      const response = await axios.get(`${API}/transporteurs`);
      console.log('Transporteurs response:', response.data);
      setTransporteurs(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des transporteurs:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur inconnue';
      console.warn(`Impossible de charger les transporteurs: ${errorMessage}`);
      // Don't show alert for transporteurs loading failure, just log it
    }
  };

  const handleTransporteurSelection = async (e) => {
    const transporteurId = e.target.value;
    setSelectedTransporteurId(transporteurId);
    
    if (transporteurId) {
      // Find selected transporteur
      const selectedTransporteur = transporteurs.find(t => t.id === transporteurId);
      if (selectedTransporteur) {
        // Auto-fill transporteur info
        setSupplierInfo({
          ...supplierInfo,
          nom_fournisseur: selectedTransporteur.name || selectedTransporteur.nom || '',
          telephone_fournisseur: selectedTransporteur.phone || selectedTransporteur.telephone || '',
          cni_fournisseur: selectedTransporteur.cni || '',
          projet: ''
        });
      }
    } else {
      // Reset when no transporteur selected
      setSupplierInfo({
        nom_fournisseur: '',
        telephone_fournisseur: '',
        cni_fournisseur: '',
        type_camion: '',
        projet: '',
        tracteur_1: '',
        semi_remorque_1: '',
        tracteur_2: '',
        semi_remorque_2: ''
      });
      setVehicles([{ id: Date.now(), tracteur: '', semi_remorque: '', fromFleet: false }]);
    }
  };



  const addVehicle = () => {
    const newVehicle = {
      id: Date.now(),
      tracteur: '',
      semi_remorque: '',
      fromFleet: false
    };
    setVehicles([...vehicles, newVehicle]);
  };

  const removeVehicle = (index) => {
    if (vehicles.length > 1) {
      const newVehicles = vehicles.filter((_, i) => i !== index);
      setVehicles(newVehicles);
    }
  };

  const updateVehicle = (index, field, value) => {
    const newVehicles = [...vehicles];
    newVehicles[index] = {
      ...newVehicles[index],
      [field]: value,
      fromFleet: newVehicles[index].fromFleet && !allowEditFleetVehicles ? newVehicles[index].fromFleet : false
    };
    setVehicles(newVehicles);
  };

  const handleFleetVehicleSelection = (vehicleId, isSelected) => {
    if (isSelected) {
      setSelectedFleetVehicles([...selectedFleetVehicles, vehicleId]);
    } else {
      setSelectedFleetVehicles(selectedFleetVehicles.filter(id => id !== vehicleId));
    }
  };

  const addSelectedFleetVehicles = () => {
    const selectedVehiclesToAdd = fleetVehicles.filter(vehicle => 
      selectedFleetVehicles.includes(vehicle.id)
    );

    const convertedVehicles = selectedVehiclesToAdd.map((vehicle, index) => ({
      id: vehicle.id || Date.now() + index,
      tracteur: vehicle.immatriculation_1 || '',
      semi_remorque: vehicle.immatriculation_2 || '',
      fromFleet: true,
      fleetVehicleId: vehicle.id,
      type_camion: vehicle.type_camion || '',
      capacite: vehicle.capacite_max_tonnes || ''
    }));

    // Remove the default empty vehicle if it exists and has no data
    const filteredCurrentVehicles = vehicles.filter(v => 
      v.tracteur || v.semi_remorque || v.fromFleet
    );

    setVehicles([...filteredCurrentVehicles, ...convertedVehicles]);
    setSelectedFleetVehicles([]);
    setShowVehicleSelector(false);
    
    alert(`${convertedVehicles.length} véhicule(s) ajouté(s) depuis la flotte`);
  };

  const loadTransporteurVehicles = async () => {
    if (!selectedTransporteurId) {
      alert('Aucun transporteur sélectionné');
      return;
    }
    
    setLoadingFleet(true);
    console.log('🚛 Loading vehicles for transporteur:', selectedTransporteurId);
    
    try {
      const token = localStorage.getItem('token');
      const url = `${API}/fleet-vehicles?transporteur_id=${selectedTransporteurId}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const fleetVehiclesData = response.data?.vehicles || [];
      
      if (fleetVehiclesData.length === 0) {
        alert('Aucun véhicule trouvé dans la flotte de ce transporteur');
        return;
      }
      
      setFleetVehicles(fleetVehiclesData);
      alert(`${fleetVehiclesData.length} véhicule(s) disponible(s) dans la flotte. Sélectionnez ceux que vous souhaitez utiliser.`);
      
    } catch (error) {
      console.error('Erreur chargement flotte:', error);
      const errorMessage = error.response?.status === 404 
        ? 'Transporteur non trouvé ou aucun véhicule dans la flotte'
        : error.response?.status === 401
        ? 'Session expirée, veuillez vous reconnecter'  
        : error.response?.data?.detail || error.message || 'Erreur inconnue';
        
      alert(`Erreur lors du chargement des véhicules de la flotte: ${errorMessage}`);
    } finally {
      setLoadingFleet(false);
    }
  };

  // Template Management Functions
  const updateTemplateStyle = async (newStyle) => {
    setLoading(true);
    try {
      await axios.put(`${API}/documents/${document.id}/template`, { template_style: newStyle });
      setTemplateStyle(newStyle);
      setShowTemplateModal(false);
      alert('Style de template mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du template:', error);
      alert('Erreur lors de la mise à jour du template');
    } finally {
      setLoading(false);
    }
  };

  // Export HTML Functions
  const exportHTML = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/${document.id}/html-preview`);
      const htmlContent = response.data;
      
      if (!htmlContent || htmlContent.trim() === '') {
        throw new Error('Contenu HTML vide reçu du serveur');
      }
      
      // Create a downloadable HTML file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const doc = window.document; // Avoid naming conflict
      const a = doc.createElement('a');
      a.href = url;
      a.download = `document_${document.reference}.html`;
      a.style.display = 'none';
      doc.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        doc.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      alert('Document HTML exporté avec succès !');
    } catch (error) {
      console.error('Erreur lors de l\'export HTML:', error);
      alert(`Erreur lors de l'export HTML: ${error.message || error.response?.data?.detail || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // HTML to PDF conversion functionality with fallback
  const convertHtmlToPdf = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/${document.id}/html-preview`);
      const htmlContent = response.data;
      
      if (!htmlContent || htmlContent.trim() === '') {
        throw new Error('Contenu HTML vide reçu du serveur');
      }
      
      // Use only the jsPDF method as it's more reliable
      await convertUsingJsPdf(htmlContent);
      
      alert('Document HTML converti en PDF avec succès !');
    } catch (error) {
      console.error('Erreur lors de la conversion HTML vers PDF:', error);
      alert(`Erreur lors de la conversion HTML vers PDF: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Method using jsPDF + html2canvas directly (more stable)
  const convertUsingJsPdf = async (htmlContent) => {
    const doc = window.document; // Avoid naming conflict
    const tempContainer = doc.createElement('div');
    tempContainer.innerHTML = htmlContent;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '210mm';
    tempContainer.style.padding = '20px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.fontFamily = 'Arial, sans-serif';
    tempContainer.style.fontSize = '12px';
    tempContainer.style.lineHeight = '1.4';
    
    doc.body.appendChild(tempContainer);
    
    try {
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      });
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${document.reference}_from_html.pdf`);
    } finally {
      doc.body.removeChild(tempContainer);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/documents/${document.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${document.reference}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const previewPDF = async () => {
    try {
      const response = await axios.get(`${API}/documents/${document.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erreur lors de l\'aperçu:', error);
      alert('Erreur lors de l\'aperçu du PDF');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-green-600">{document.reference}</h3>
          <p className="text-gray-600">
            {document.type === 'devis' ? 'Devis / Cotation' : 
             document.type === 'bon_commande' ? 'Bon de commande' : 'Facture'}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {/* Row 1: Main Actions */}
          <div className="flex flex-wrap gap-2">
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowTemplateModal(true)}
                className="bg-white text-indigo-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-1 border-r border-gray-200"
                title="Changer le template"
              >
                🎨 {templateStyle === 'advanced' ? 'Avancé' : 'Standard'}
              </button>
              <button
                onClick={previewHTML}
                disabled={loading}
                className="bg-white text-blue-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 border-r border-gray-200 flex items-center gap-1"
                title="Aperçu HTML"
              >
                🌐 Aperçu
              </button>
              <button
                onClick={exportHTML}
                disabled={loading}
                className="bg-white text-purple-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                title="Exporter en HTML"
              >
                💾 Export
              </button>
            </div>
            
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={convertHtmlToPdf}
                disabled={loading}
                className="bg-white text-green-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1 border-r border-gray-200"
                title="Télécharger PDF"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Télécharger
              </button>
              <button
                onClick={handleDuplicateDocument}
                className="bg-white text-purple-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-1"
                title="Dupliquer le document"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Dupliquer
              </button>
              
              {/* 🔄 NOUVEAU BOUTON: Recalculer avec formule standard */}
              <button
                onClick={recalculateDocument}
                disabled={loading}
                className="bg-white text-blue-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                title="Recalculer les totaux avec la formule standard comptable"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? 'Recalcul...' : 'Recalculer'}
              </button>
            </div>
          </div>

          {/* Row 2: Generation Actions (only for devis) */}
          {document.type === 'devis' && (
            <div className="flex flex-wrap gap-2">
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => generateDocument('bon_commande')}
                  disabled={loading || documentStatus !== 'acceptée'}
                  className="bg-white text-orange-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1 border-r border-gray-200"
                  title="Générer Bon de Commande"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Bon de Commande
                </button>
                <button
                  onClick={() => generateDocument('facture')}
                  disabled={loading || documentStatus !== 'acceptée'}
                  className="bg-white text-purple-600 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                  title="Générer Facture"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                  </svg>
                  Facture
                </button>
              </div>
              
              {/* Bouton Modifier */}
              {onEditDocument && (
                <button
                  onClick={() => onEditDocument(document)}
                  className="text-blue-600 hover:text-blue-700 px-3 py-1.5 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1"
                  title="Modifier le document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Modifier
                </button>
              )}
              
              <button
                onClick={onBack}
                className="text-green-600 hover:text-green-700 px-3 py-1.5 text-sm border border-green-300 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-1"
              >
                ← Retour
              </button>
            </div>
          )}

          {/* Row 2: Edit + Back button (for non-devis documents) */}
          {document.type !== 'devis' && (
            <div className="flex justify-end gap-2">
              {/* Bouton Modifier */}
              {onEditDocument && (
                <button
                  onClick={() => onEditDocument(document)}
                  className="text-blue-600 hover:text-blue-700 px-3 py-1.5 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1"
                  title="Modifier le document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Modifier
                </button>
              )}
              
              <button
                onClick={onBack}
                className="text-green-600 hover:text-green-700 px-3 py-1.5 text-sm border border-green-300 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-1"
              >
                ← Retour
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status Management Section */}
      {document.type === 'devis' && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">Statut du document</h4>
              <p className="text-sm text-gray-600">
                Statut actuel: <span className={`font-medium ${documentStatus === 'acceptée' ? 'text-green-600' : 'text-orange-600'}`}>
                  {documentStatus === 'acceptée' ? 'Accepté' : 'En cours'}
                </span>
              </p>
            </div>
            {documentStatus !== 'acceptée' && (
              <button
                onClick={() => updateStatus('acceptée')}
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                ✅ Marquer comme accepté
              </button>
            )}
          </div>
        </div>
      )}

      {/* Linked Documents Section */}
      {linkedDocuments.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Documents liés</h4>
          <div className="space-y-2">
            {linkedDocuments.map((linkedDoc, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                <div>
                  <span className="font-medium">{linkedDoc.reference}</span>
                  <span className="ml-2 text-sm text-gray-600">
                    ({linkedDoc.type === 'bon_commande' ? 'Bon de Commande' : 'Facture'})
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(linkedDoc.date).toLocaleDateString('fr-FR')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('details')}
          className={`px-6 py-3 font-medium text-sm border-b-2 ${
            activeTab === 'details'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📄 Détails
        </button>
        <button
          onClick={() => setActiveTab('pricing')}
          className={`px-6 py-3 font-medium text-sm border-b-2 ${
            activeTab === 'pricing'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          💰 Tarification
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 font-medium text-sm border-b-2 ${
            activeTab === 'history'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📋 Historique
        </button>
        {linkedDocuments.length > 0 && (
          <button
            onClick={() => setActiveTab('linked')}
            className={`px-6 py-3 font-medium text-sm border-b-2 ${
              activeTab === 'linked'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔗 Documents liés ({linkedDocuments.length})
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informations générales */}
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Informations générales</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Référence:</span>
                    <span className="font-medium">{document.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">
                      {document.type === 'devis' ? 'Devis / Cotation' : 
                       document.type === 'bon_commande' ? 'Bon de commande' : 'Facture'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{new Date(document.date).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Émetteur:</span>
                    <span className="font-medium">{document.emetteur}</span>
                  </div>
                  {document.suivi_par && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Suivi par:</span>
                      <span className="font-medium">{document.suivi_par}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Informations client */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Client</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Nom:</strong> {document.client.name}</div>
                  <div><strong>Adresse:</strong> {document.client.address}</div>
                  {document.client.rccm && <div><strong>RCCM:</strong> {document.client.rccm}</div>}
                  {document.client.contact && <div><strong>Contact:</strong> {document.client.contact}</div>}
                  {document.client.email && <div><strong>Email:</strong> {document.client.email}</div>}
                  {document.client.phone && <div><strong>Téléphone:</strong> {document.client.phone}</div>}
                </div>
              </div>
            </div>

            {/* Détails financiers */}
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Totaux</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total HT:</span>
                    <span className="font-medium">{formatCurrency(document.total_ht)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total TVA:</span>
                    <span className="font-medium">{formatCurrency(document.total_tva)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-baobab-primary">
                    <span>Total TTC:</span>
                    <span>{formatCurrency(document.total_ttc)}</span>
                  </div>
                </div>
              </div>

              {/* Conditions et délais */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Conditions</h4>
                <div className="space-y-2 text-sm">
                  {document.conditions && (
                    <div>
                      <strong>Conditions de règlement:</strong>
                      <p className="mt-1 text-gray-700">{document.conditions}</p>
                    </div>
                  )}
                  {document.delai_retractation && (
                    <div>
                      <strong>Délai de rétractation:</strong>
                      <p className="mt-1 text-gray-700">{document.delai_retractation}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Articles */}
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Articles / Prestations</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-baobab-primary text-white">
                    {document.type !== 'devis' && <th className="border border-gray-300 p-3 text-left">Référence</th>}
                    <th className="border border-gray-300 p-3 text-left">Description</th>
                    <th className="border border-gray-300 p-3 text-left">Quantité</th>
                    <th className="border border-gray-300 p-3 text-left">Unité</th>
                    <th className="border border-gray-300 p-3 text-left">Prix unitaire</th>
                    <th className="border border-gray-300 p-3 text-left">Total HT</th>
                    {document.type !== 'devis' && <th className="border border-gray-300 p-3 text-left">TVA</th>}
                    {document.type !== 'devis' && <th className="border border-gray-300 p-3 text-left">Total TTC</th>}
                    {document.type === 'devis' && <th className="border border-gray-300 p-3 text-left">Commentaire</th>}
                  </tr>
                </thead>
                <tbody>
                  {document.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {document.type !== 'devis' && <td className="border border-gray-300 p-3">{item.ref}</td>}
                      <td className="border border-gray-300 p-3">{item.description}</td>
                      <td className="border border-gray-300 p-3">{item.quantity}</td>
                      <td className="border border-gray-300 p-3">{item.unit}</td>
                      <td className="border border-gray-300 p-3">{formatCurrency(item.unit_price)}</td>
                      <td className="border border-gray-300 p-3 font-medium">{formatCurrency(item.total_ht)}</td>
                      {document.type !== 'devis' && <td className="border border-gray-300 p-3">{item.tva_rate}%</td>}
                      {document.type !== 'devis' && <td className="border border-gray-300 p-3 font-medium">{formatCurrency(item.total_ttc)}</td>}
                      {document.type === 'devis' && <td className="border border-gray-300 p-3">{item.comment}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sections spécifiques au devis */}
          {document.type === 'devis' && (
            <div className="mt-6 space-y-4">
              {document.objet && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Objet</h4>
                  <p className="text-sm text-gray-700">{document.objet}</p>
                </div>
              )}
              
              {document.inclus && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Inclus dans l'offre</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-line">{document.inclus}</div>
                </div>
              )}
              
              {document.exclus && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Exclus de l'offre</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-line">{document.exclus}</div>
                </div>
              )}
              
              {document.notes && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Notes générales</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-line">{document.notes}</div>
                </div>
              )}

              {/* Modalités de paiement */}
              {document.modalites_paiement && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Modalités de paiement</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-line">{document.modalites_paiement}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <div>
          <DocumentPricingDetails document={document} />
        </div>
      )}

      {/* Activity History Tab */}
      {activeTab === 'history' && (
        <div>
          <ActivityTimeline 
            documentType="devis" 
            documentId={document.id}
            showFilters={true}
            maxItems={50}
          />
        </div>
      )}

      {/* Linked Documents Tab */}
      {activeTab === 'linked' && (
        <div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Documents liés</h3>
              <p className="text-sm text-gray-600">{linkedDocuments.length} document(s) trouvé(s)</p>
            </div>
            
            {linkedDocuments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Aucun document lié trouvé.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {linkedDocuments.map((linkedDoc, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{linkedDoc.reference}</h4>
                        <p className="text-sm text-gray-600">
                          {linkedDoc.type === 'bon_commande' ? 'Bon de Commande' : 'Facture'}
                        </p>
                        <p className="text-sm text-gray-500">
                          Créé le {new Date(linkedDoc.date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {linkedDoc.total_ttc ? `${linkedDoc.total_ttc.toLocaleString()} FCFA` : 'N/A'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {linkedDoc.parent_document_id ? 'Document dérivé' : 'Document principal'}
                        </p>
                      </div>
                    </div>
                    {linkedDoc.parent_document_id && (
                      <div className="mt-2 text-xs text-blue-600">
                        ← Généré depuis: {document.reference}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HTML Preview Modal */}
      {showHtmlPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Aperçu HTML - {document.reference}</h3>
              <button
                onClick={() => setShowHtmlPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </div>
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowHtmlPreview(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Choisir le style de template</h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="template"
                    value="standard"
                    checked={templateStyle === 'standard'}
                    onChange={() => setTemplateStyle('standard')}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">Template Standard</div>
                    <div className="text-sm text-gray-600">
                      Template classique avec mise en page traditionnelle
                    </div>
                  </div>
                </label>
                
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="template"
                    value="advanced"
                    checked={templateStyle === 'advanced'}
                    onChange={() => setTemplateStyle('advanced')}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">Template Avancé</div>
                    <div className="text-sm text-gray-600">
                      Template professionnel avec design moderne et sections détaillées
                    </div>
                  </div>
                </label>
              </div>
              
              <div className="text-sm text-gray-500 p-3 bg-blue-50 rounded-lg">
                <strong>Aperçu des différences :</strong>
                <ul className="mt-2 space-y-1">
                  <li>• <strong>Standard:</strong> Mise en page simple et claire</li>
                  <li>• <strong>Avancé:</strong> Design professionnel avec sections détaillées, signatures, conditions étendues</li>
                </ul>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={() => updateTemplateStyle(templateStyle)}
                disabled={loading}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Mise à jour...' : 'Appliquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Generation Modal */}
      {showGenerationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Informations Transporteur - Bon de Commande</h3>
              <button
                onClick={() => setShowGenerationModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Sélection transporteur existant */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Sélection du Transporteur</h4>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Choisir un transporteur existant (optionnel)
                  </label>
                  <select
                    value={selectedTransporteurId}
                    onChange={handleTransporteurSelection}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  >
                    <option value="">-- Nouveau transporteur ou saisie manuelle --</option>
                    {transporteurs.map((transporteur) => (
                      <option key={transporteur.id} value={transporteur.id}>
                        {transporteur.name} - {transporteur.phone || 'Pas de téléphone'}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Informations transporteur */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du transporteur *
                    </label>
                    <input
                      type="text"
                      value={supplierInfo.nom_fournisseur}
                      onChange={(e) => setSupplierInfo({...supplierInfo, nom_fournisseur: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      placeholder="Nom du transporteur"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone du transporteur *
                    </label>
                    <input
                      type="text"
                      value={supplierInfo.telephone_fournisseur}
                      onChange={(e) => setSupplierInfo({...supplierInfo, telephone_fournisseur: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      placeholder="Téléphone du transporteur"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNI du transporteur
                    </label>
                    <input
                      type="text"
                      value={supplierInfo.cni_fournisseur}
                      onChange={(e) => setSupplierInfo({...supplierInfo, cni_fournisseur: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      placeholder="CNI du transporteur"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Projet
                    </label>
                    <input
                      type="text"
                      value={supplierInfo.projet}
                      onChange={(e) => setSupplierInfo({...supplierInfo, projet: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      placeholder="Nom du projet"
                    />
                  </div>
                </div>
              </div>

              {/* Gestion des véhicules */}
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">🚛 Véhicules Affectés</h4>
                  <div className="space-x-2">
                    {selectedTransporteurId && (
                      <button
                        onClick={() => setShowVehicleSelector(!showVehicleSelector)}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        📋 Sélectionner depuis la flotte
                      </button>
                    )}
                    <button
                      onClick={addVehicle}
                      className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                    >
                      ➕ Ajouter véhicule manuel
                    </button>
                  </div>
                </div>

                {/* Vehicle selector modal */}
                {showVehicleSelector && selectedTransporteurId && (
                  <div className="mb-4 p-3 bg-blue-50 border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-blue-800">Sélectionner véhicules de la flotte</h5>
                      <button
                        onClick={() => setShowVehicleSelector(false)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ✕
                      </button>
                    </div>
                    
                    {!fleetVehicles.length ? (
                      <div className="text-center py-2">
                        <button
                          onClick={loadTransporteurVehicles}
                          disabled={loadingFleet}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                          {loadingFleet ? '⏳ Chargement...' : '📋 Charger flotte'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-3 text-sm text-blue-600">
                          Sélectionnez les véhicules à utiliser ({fleetVehicles.length} disponibles) :
                        </div>
                        
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {fleetVehicles.map((vehicle) => (
                            <label key={vehicle.id} className="flex items-start p-2 bg-white rounded border hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedFleetVehicles.includes(vehicle.id)}
                                onChange={(e) => handleFleetVehicleSelection(vehicle.id, e.target.checked)}
                                className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  Tracteur: {vehicle.immatriculation_1 || 'Non spécifié'}
                                  {vehicle.immatriculation_2 && (
                                    <span className="ml-2">| Remorque: {vehicle.immatriculation_2}</span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {vehicle.type_camion && `Type: ${vehicle.type_camion}`}
                                  {vehicle.capacite_max_tonnes && ` | Capacité: ${vehicle.capacite_max_tonnes}T`}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {selectedFleetVehicles.length} véhicule(s) sélectionné(s)
                          </span>
                          <div className="space-x-2">
                            <button
                              onClick={() => setSelectedFleetVehicles([])}
                              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                            >
                              Tout désélectionner
                            </button>
                            <button
                              onClick={() => setSelectedFleetVehicles(fleetVehicles.map(v => v.id))}
                              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              Tout sélectionner
                            </button>
                            <button
                              onClick={addSelectedFleetVehicles}
                              disabled={selectedFleetVehicles.length === 0}
                              className="px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              ➕ Ajouter sélectionnés
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {vehicles.length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <p>Aucun véhicule ajouté.</p>
                    <p className="text-sm">Utilisez "Sélectionner depuis la flotte" ou "Ajouter véhicule manuel" pour commencer.</p>
                  </div>
                )}
                
                {vehicles.map((vehicle, index) => (
                  <div key={vehicle.id || index} className={`mb-4 p-3 bg-white rounded border-l-4 ${index === 0 ? 'border-green-500' : 'border-green-300'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-gray-800">
                        Camion {index + 1} {index === 0 ? '' : '(optionnel)'}
                      </h5>
                      <div className="flex items-center space-x-2">
                        {vehicle.fromFleet && (
                          <span className="text-xs text-green-600 flex items-center">
                            <span className="mr-1">✓</span>
                            Depuis la flotte
                          </span>
                        )}
                        {vehicles.length > 1 && (
                          <button
                            onClick={() => removeVehicle(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                            title="Supprimer ce véhicule"
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tracteur (ex: 3711EL01)
                        </label>
                        <input
                          type="text"
                          value={vehicle.tracteur || ''}
                          onChange={(e) => updateVehicle(index, 'tracteur', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                          placeholder={`Numéro tracteur ${index + 1}`}
                          disabled={vehicle.fromFleet && !allowEditFleetVehicles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Semi remorque (ex: 8708AY01)
                        </label>
                        <input
                          type="text"
                          value={vehicle.semi_remorque || ''}
                          onChange={(e) => updateVehicle(index, 'semi_remorque', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                          placeholder={`Numéro semi-remorque ${index + 1}`}
                          disabled={vehicle.fromFleet && !allowEditFleetVehicles}
                        />
                      </div>
                    </div>
                    {vehicle.fromFleet && !allowEditFleetVehicles && (
                      <div className="mt-2 text-xs text-blue-600">
                        💡 <button 
                          onClick={() => setAllowEditFleetVehicles(true)} 
                          className="underline hover:text-blue-800"
                        >
                          Cliquez ici pour modifier les véhicules de la flotte
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Option de sauvegarde */}
              {selectedTransporteurId && vehicles.some(v => !v.fromFleet) && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={saveToFleet}
                      onChange={(e) => setSaveToFleet(e.target.checked)}
                      className="mr-2 h-4 w-4 text-baobab-primary focus:ring-baobab-primary border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Sauvegarder les nouveaux véhicules dans la flotte du transporteur
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Les véhicules seront automatiquement disponibles pour les prochains bons de commande.
                  </p>
                </div>
              )}

              {/* Note d'information */}
              <div className="bg-yellow-50 p-3 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Information
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Le bon de commande sera envoyé au transporteur avec tous les détails des véhicules à affecter pour cette mission.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowGenerationModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={handleGenerationSubmit}
                disabled={loading}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Génération...' : 'Générer Bon de Commande'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentView;